import { useRuntimeBrand } from '@core/hooks/useRuntimeBrand';
import { useIndustry } from '@/app/providers/industry-context.js';
import defaultLogo from '@/assets/default-logo.svg';
import groceryDefaultLogo from '@/assets/logo-grocery-default.svg';
import fishDefaultLogo from '@/assets/logo-fish-default.svg';

const themeMarks = {
  default: { letter: 'B', gradient: 'linear-gradient(135deg, #1677ff, #4096ff)' },
  demo: { letter: 'B', gradient: 'linear-gradient(135deg, #1677ff, #4096ff)' },
  meat: { letter: 'M', gradient: 'linear-gradient(135deg, #7f1d1d, #ef4444)' },
  fish: { letter: '~', gradient: 'linear-gradient(135deg, #0647a6, #00b8c8)' },
  restaurant: { letter: 'S', gradient: 'linear-gradient(135deg, #92400e, #f59e0b)' },
  publication: { letter: 'Q', gradient: 'linear-gradient(135deg, #111827, #4b5563)' },
};

function GroceryMark() {
  return (
    <svg
      viewBox="0 0 40 40"
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', height: '100%', display: 'block' }}
    >
      <defs>
        <radialGradient id="auth-orange-fill" cx="35%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#fdba74" />
          <stop offset="55%" stopColor="#f97316" />
          <stop offset="100%" stopColor="#c2410c" />
        </radialGradient>
        <linearGradient id="auth-leaf-fill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#86efac" />
          <stop offset="100%" stopColor="#16a34a" />
        </linearGradient>
      </defs>
      <circle cx="20" cy="22" r="14" fill="url(#auth-orange-fill)" />
      <circle cx="14" cy="17" r="3" fill="rgba(255,255,255,0.32)" />
      <path d="M20 8 C24 4, 30 4, 32 6 C30 10, 26 12, 22 11 Z" fill="url(#auth-leaf-fill)" />
      <path d="M22 11 C24 9, 27 7, 30 6" stroke="#15803d" strokeWidth="0.6" fill="none" />
    </svg>
  );
}

const fallbackLogosByIndustry = {
  grocery: groceryDefaultLogo,
  fish: fishDefaultLogo,
  default: defaultLogo,
};

export default function AuthBrandMark({ compact = false }) {
  const { key, config } = useIndustry();
  const { logoUrl: runtimeLogoUrl, brandName: runtimeBrandName } = useRuntimeBrand();
  const name = runtimeBrandName || config?.copy?.appName || config?.name || 'Store';
  const tagline = config?.copy?.tagline;
  const mark = themeMarks[key] ?? themeMarks.default;
  const fallbackLogoUrl = fallbackLogosByIndustry[key] || fallbackLogosByIndustry.default;
  const logoUrl = runtimeLogoUrl || config?.logoUrl || fallbackLogoUrl;

  return (
    <div
      style={{
        display: 'grid',
        justifyItems: 'center',
        gap: compact ? '0.375rem' : '0.5rem',
        textAlign: 'center',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: compact ? '2.5rem' : '3rem',
          height: compact ? '2.5rem' : '3rem',
          borderRadius: '0.5rem',
          display: 'grid',
          placeItems: 'center',
          overflow: 'hidden',
          color: '#ffffff',
          fontSize: compact ? '1.35rem' : '1.65rem',
          fontWeight: 900,
          lineHeight: 1,
        }}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              background: 'transparent',
            }}
          />
        ) : key === 'grocery' ? (
          <GroceryMark />
        ) : (
          mark.letter
        )}
      </span>
      <span style={{ display: 'grid', gap: '0.125rem' }}>
        <strong style={{ color: 'var(--color-text)', fontSize: '1rem', lineHeight: 1.1 }}>
          {name}
        </strong>
        {!compact && tagline ? (
          <small
            style={{
              color: 'var(--color-text-secondary)',
              fontSize: '0.75rem',
              lineHeight: 1.25,
            }}
          >
            {tagline}
          </small>
        ) : null}
      </span>
    </div>
  );
}
